/**
 * ShopsPage — list user's shops, manage sections, scan, view results.
 * Includes: profile editing, visibility toggle, scan history per section.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useShopStore, Shop, ScanRecord } from '../../stores/shopStore';
import './ShopsPage.css';

const COUNTRY_CODES = [
  { code: '+971', flag: '🇦🇪', name: 'UAE' },
  { code: '+966', flag: '🇸🇦', name: 'Saudi' },
  { code: '+968', flag: '🇴🇲', name: 'Oman' },
  { code: '+973', flag: '🇧🇭', name: 'Bahrain' },
  { code: '+974', flag: '🇶🇦', name: 'Qatar' },
  { code: '+965', flag: '🇰🇼', name: 'Kuwait' },
  { code: '+962', flag: '🇯🇴', name: 'Jordan' },
  { code: '+961', flag: '🇱🇧', name: 'Lebanon' },
  { code: '+964', flag: '🇮🇶', name: 'Iraq' },
  { code: '+20', flag: '🇪🇬', name: 'Egypt' },
  { code: '+90', flag: '🇹🇷', name: 'Turkey' },
  { code: '+91', flag: '🇮🇳', name: 'India' },
  { code: '+92', flag: '🇵🇰', name: 'Pakistan' },
  { code: '+63', flag: '🇵🇭', name: 'Philippines' },
  { code: '+44', flag: '🇬🇧', name: 'UK' },
  { code: '+1', flag: '🇺🇸', name: 'US' },
];

/** Extract country code from a stored number like "+971501234567" */
function parseStoredNumber(full: string): { code: string; local: string } {
  if (!full) return { code: '+971', local: '' };
  for (const cc of COUNTRY_CODES) {
    if (full.startsWith(cc.code)) {
      return { code: cc.code, local: full.slice(cc.code.length) };
    }
  }
  return { code: '+971', local: full.replace(/^\+/, '') };
}

export default function ShopsPage() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const updateProfile = useAuthStore((s) => s.updateProfile);
  const navigate = useNavigate();
  const { shops, loading, scans, fetchShops, createShop, deleteShop, addSection, updateShop, fetchShopScans } = useShopStore();

  const [showAddShop, setShowAddShop] = useState(false);
  const [newShopName, setNewShopName] = useState('');
  const [newShopAddress, setNewShopAddress] = useState('');
  const [expandedShop, setExpandedShop] = useState<string | null>(null);
  const [newSectionName, setNewSectionName] = useState('');
  const [showProfile, setShowProfile] = useState(false);
  const parsedPhone = parseStoredNumber(user?.phone || '');
  const parsedWhatsapp = parseStoredNumber(user?.whatsapp || '');
  const [phoneCode, setPhoneCode] = useState(parsedPhone.code);
  const [phone, setPhone] = useState(parsedPhone.local);
  const [waCode, setWaCode] = useState(parsedWhatsapp.code);
  const [whatsapp, setWhatsapp] = useState(parsedWhatsapp.local);
  const [sameAsPhone, setSameAsPhone] = useState(true);

  useEffect(() => {
    fetchShops();
  }, []);

  // Fetch scans when a shop is expanded
  useEffect(() => {
    if (expandedShop) {
      fetchShopScans(expandedShop);
    }
  }, [expandedShop]);

  async function handleCreateShop(e: React.FormEvent) {
    e.preventDefault();
    if (!newShopName.trim()) return;
    const shop = await createShop(newShopName.trim(), newShopAddress.trim());
    if (shop) {
      setNewShopName('');
      setNewShopAddress('');
      setShowAddShop(false);
      setExpandedShop(shop.id);
    }
  }

  async function handleAddSection(shopId: string) {
    if (!newSectionName.trim()) return;
    await addSection(shopId, newSectionName.trim());
    setNewSectionName('');
  }

  function handleScan(shopId: string, sectionId: string, sectionName: string) {
    const params = new URLSearchParams({ shopId, sectionId, sectionName });
    navigate(`/scan?${params.toString()}`);
  }

  async function handleSaveProfile(e: React.FormEvent) {
    e.preventDefault();
    const fullPhone = phone ? `${phoneCode}${phone}` : '';
    const fullWhatsapp = sameAsPhone ? fullPhone : (whatsapp ? `${waCode}${whatsapp}` : '');
    await updateProfile(fullPhone, fullWhatsapp);
    setShowProfile(false);
  }

  async function handleToggleVisibility(shop: Shop) {
    const newVis = shop.visibility === 'public' ? 'private' : 'public';
    await updateShop(shop.id, { visibility: newVis });
  }

  return (
    <div className="shops-page">
      {/* Header */}
      <header className="shops-header">
        <div className="shops-header-left">
          <span className="shops-logo">🏪</span>
          <h1>My Shops</h1>
        </div>
        <div className="shops-header-right">
          {user?.picture ? (
            <img
              src={user.picture}
              className="shops-avatar"
              alt=""
              referrerPolicy="no-referrer"
              onClick={() => setShowProfile(!showProfile)}
            />
          ) : (
            <span className="shops-avatar-fallback" onClick={() => setShowProfile(!showProfile)}>
              {user?.name?.charAt(0) || '?'}
            </span>
          )}
          <button className="shops-btn-text" onClick={() => { logout(); navigate('/login', { replace: true }); }}>Sign Out</button>
        </div>
      </header>

      {/* WhatsApp prompt — shows when phone is empty */}
      {user && !user.phone && !showProfile && (
        <form className="whatsapp-prompt" onSubmit={handleSaveProfile}>
          <div className="whatsapp-prompt-header">
            <svg className="whatsapp-icon" viewBox="0 0 24 24" fill="#25D366" width="28" height="28">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.05 21.785h-.01a9.865 9.865 0 01-5.031-1.378l-.361-.214-3.741.981.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884zM20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.334.101 11.893c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.585 0 11.946-5.336 11.949-11.896 0-3.176-1.24-6.165-3.495-8.411"/>
            </svg>
            <div className="whatsapp-prompt-text">
              <strong>Complete your profile</strong>
              <span>Add your WhatsApp so customers can contact you</span>
            </div>
          </div>
          <div className="phone-row">
            <select value={phoneCode} onChange={(e) => setPhoneCode(e.target.value)} className="country-code-select">
              {COUNTRY_CODES.map((c) => (
                <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
              ))}
            </select>
            <input
              type="tel"
              placeholder="Phone number"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/[^0-9]/g, ''))}
              className="shops-input shops-input-sm phone-input"
            />
          </div>
          <label className="same-number-check">
            <input type="checkbox" checked={sameAsPhone} onChange={(e) => setSameAsPhone(e.target.checked)} />
            <span>WhatsApp same as phone</span>
          </label>
          {!sameAsPhone && (
            <div className="phone-row">
              <select value={waCode} onChange={(e) => setWaCode(e.target.value)} className="country-code-select">
                {COUNTRY_CODES.map((c) => (
                  <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                ))}
              </select>
              <input
                type="tel"
                placeholder="WhatsApp number"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value.replace(/[^0-9]/g, ''))}
                className="shops-input shops-input-sm phone-input"
              />
            </div>
          )}
          <button type="submit" className="whatsapp-prompt-btn">Save</button>
        </form>
      )}

      {/* Profile Panel (avatar tap — for editing after initial setup) */}
      {showProfile && (
        <form className="profile-panel" onSubmit={handleSaveProfile}>
          <div className="profile-info">
            <strong>{user?.name}</strong>
            <span className="profile-email">{user?.email}</span>
          </div>
          <div className="phone-row">
            <select value={phoneCode} onChange={(e) => setPhoneCode(e.target.value)} className="country-code-select">
              {COUNTRY_CODES.map((c) => (
                <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
              ))}
            </select>
            <input
              type="tel"
              placeholder="Phone number"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/[^0-9]/g, ''))}
              className="shops-input shops-input-sm phone-input"
            />
          </div>
          <label className="same-number-check">
            <input type="checkbox" checked={sameAsPhone} onChange={(e) => setSameAsPhone(e.target.checked)} />
            <span>WhatsApp same as phone</span>
          </label>
          {!sameAsPhone && (
            <div className="phone-row">
              <select value={waCode} onChange={(e) => setWaCode(e.target.value)} className="country-code-select">
                {COUNTRY_CODES.map((c) => (
                  <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                ))}
              </select>
              <input
                type="tel"
                placeholder="WhatsApp number"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value.replace(/[^0-9]/g, ''))}
                className="shops-input shops-input-sm phone-input"
              />
            </div>
          )}
          <div className="shops-add-actions">
            <button type="submit" className="shops-btn-primary shops-btn-sm">Save</button>
            <button type="button" className="shops-btn-text" onClick={() => setShowProfile(false)}>Close</button>
          </div>
        </form>
      )}

      {/* Add Shop */}
      {!showAddShop ? (
        <button className="shops-btn-add" onClick={() => setShowAddShop(true)}>
          + Add New Shop
        </button>
      ) : (
        <form className="shops-add-form" onSubmit={handleCreateShop}>
          <input
            type="text"
            placeholder="Shop name"
            value={newShopName}
            onChange={(e) => setNewShopName(e.target.value)}
            autoFocus
            className="shops-input"
          />
          <input
            type="text"
            placeholder="Address (optional)"
            value={newShopAddress}
            onChange={(e) => setNewShopAddress(e.target.value)}
            className="shops-input"
          />
          <div className="shops-add-actions">
            <button type="submit" className="shops-btn-primary">Create</button>
            <button type="button" className="shops-btn-text" onClick={() => setShowAddShop(false)}>Cancel</button>
          </div>
        </form>
      )}

      {/* Shop List */}
      {loading && shops.length === 0 ? (
        <div className="shops-empty">Loading...</div>
      ) : shops.length === 0 ? (
        <div className="shops-empty">
          <p>No shops yet</p>
          <p className="shops-empty-sub">Tap "Add New Shop" to create your first shop</p>
        </div>
      ) : (
        <div className="shops-list">
          {shops.map((shop) => (
            <ShopCard
              key={shop.id}
              shop={shop}
              expanded={expandedShop === shop.id}
              scans={scans[shop.id] || []}
              onToggle={() => setExpandedShop(expandedShop === shop.id ? null : shop.id)}
              onDelete={() => deleteShop(shop.id)}
              onScan={handleScan}
              onToggleVisibility={() => handleToggleVisibility(shop)}
              newSectionName={expandedShop === shop.id ? newSectionName : ''}
              onSectionNameChange={setNewSectionName}
              onAddSection={() => handleAddSection(shop.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}


/* ─── Status badge helper ───────────────────────────────────────── */
function statusBadge(status: string) {
  const map: Record<string, { label: string; cls: string }> = {
    uploading: { label: '⬆ Uploading', cls: 'badge-uploading' },
    queued:    { label: '⏳ Queued',     cls: 'badge-queued' },
    processing:{ label: '⚙ Processing', cls: 'badge-processing' },
    ready:     { label: '✓ Ready',      cls: 'badge-ready' },
    failed:    { label: '✗ Failed',     cls: 'badge-failed' },
  };
  const info = map[status] || { label: status, cls: '' };
  return <span className={`scan-badge ${info.cls}`}>{info.label}</span>;
}


/* ─── Shop Card ─────────────────────────────────────────────────── */
function ShopCard({
  shop,
  expanded,
  scans,
  onToggle,
  onDelete,
  onScan,
  onToggleVisibility,
  newSectionName,
  onSectionNameChange,
  onAddSection,
}: {
  shop: Shop;
  expanded: boolean;
  scans: ScanRecord[];
  onToggle: () => void;
  onDelete: () => void;
  onScan: (shopId: string, sectionId: string, sectionName: string) => void;
  onToggleVisibility: () => void;
  newSectionName: string;
  onSectionNameChange: (v: string) => void;
  onAddSection: () => void;
}) {
  // Group scans by sectionId
  const scansBySection: Record<string, ScanRecord[]> = {};
  for (const scan of scans) {
    if (!scansBySection[scan.sectionId]) scansBySection[scan.sectionId] = [];
    scansBySection[scan.sectionId].push(scan);
  }

  const isPublic = shop.visibility === 'public';

  return (
    <div className={`shop-card ${expanded ? 'shop-card--expanded' : ''}`}>
      <div className="shop-card-header" onClick={onToggle}>
        <div>
          <h3 className="shop-card-name">
            {shop.name}
            {isPublic && <span className="visibility-badge visibility-public">Public</span>}
          </h3>
          {shop.address && <p className="shop-card-address">{shop.address}</p>}
          <p className="shop-card-meta">
            {shop.sections.length} section{shop.sections.length !== 1 ? 's' : ''}
            {scans.length > 0 && ` · ${scans.length} scan${scans.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <span className="shop-card-chevron">{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && (
        <div className="shop-card-body">
          {/* Visibility Toggle */}
          <div className="shop-visibility-row">
            <span className="shop-visibility-label">
              {isPublic ? '🌐 Public — anyone with link can view' : '🔒 Private'}
            </span>
            <button
              className={`shops-btn-small ${isPublic ? 'shops-btn-active' : ''}`}
              onClick={onToggleVisibility}
            >
              {isPublic ? 'Make Private' : 'Make Public'}
            </button>
          </div>

          {/* Share Link (if public) */}
          {isPublic && (
            <div className="shop-share-row">
              <span className="shop-share-label">Share link:</span>
              <code className="shop-share-link">
                {window.location.origin}/shop/{shop.id}
              </code>
              <button
                className="shop-share-copy-btn"
                onClick={() => {
                  const url = `${window.location.origin}/shop/${shop.id}`;
                  navigator.clipboard.writeText(url).then(() => {
                    // Brief visual feedback
                    const btn = document.activeElement as HTMLButtonElement;
                    const orig = btn.textContent;
                    btn.textContent = 'Copied!';
                    setTimeout(() => { btn.textContent = orig; }, 1500);
                  });
                }}
              >
                Copy
              </button>
            </div>
          )}

          {/* Sections */}
          {shop.sections.length === 0 ? (
            <p className="shop-sections-empty">No sections yet — add one to start scanning</p>
          ) : (
            <div className="shop-sections">
              {shop.sections.map((sec) => {
                const sectionScans = scansBySection[sec.sectionId] || [];
                const latestReady = sectionScans.find((s) => s.status === 'ready');
                const latestActive = sectionScans.find((s) =>
                  ['uploading', 'queued', 'processing'].includes(s.status)
                );
                return (
                  <div key={sec.sectionId} className="shop-section-item">
                    <div className="shop-section-info">
                      <span className="shop-section-name">{sec.name}</span>
                      {latestActive && (
                        <div className="shop-section-status">{statusBadge(latestActive.status)}</div>
                      )}
                      {!latestActive && latestReady && (
                        <div className="shop-section-status">{statusBadge('ready')}</div>
                      )}
                    </div>
                    <div className="shop-section-actions">
                      {latestReady && (
                        <a
                          className="shops-btn-view"
                          href={`/?shopid=${latestReady.id}_SMART_SECTIONS_100`}
                          target="_blank"
                          rel="noopener"
                        >
                          👁 View
                        </a>
                      )}
                      <button
                        className="shops-btn-scan"
                        onClick={() => onScan(shop.id, sec.sectionId, sec.name)}
                      >
                        📷 Scan
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add Section */}
          <div className="shop-add-section">
            <input
              type="text"
              placeholder="New section name"
              value={newSectionName}
              onChange={(e) => onSectionNameChange(e.target.value)}
              className="shops-input shops-input-sm"
              onKeyDown={(e) => e.key === 'Enter' && onAddSection()}
            />
            <button className="shops-btn-small" onClick={onAddSection}>Add</button>
          </div>

          {/* Delete */}
          <button className="shops-btn-danger" onClick={onDelete}>Delete Shop</button>
        </div>
      )}
    </div>
  );
}
